// V2 Tempo — Squad Mastery. Per-archetype usage counter that survives
// W/L outcomes: every Recon you produce ticks Recon mastery, regardless
// of whether you won the match. The mastery ladder gives the player
// permanent ownership of *how they play* — "I'm an Assault main, Lv 3
// Mastery" — independent of the volatile ELO ladder.
//
// Why this matters for D7:
//   - ELO can fluctuate. Mastery only goes UP.
//   - Players love identity. "Assault main" is more sticky than "Lv 8."
//   - Gives a reason to try a different archetype next match ("I'm one
//     Engineer away from Recruit Engineer mastery").
//
// Why client-side only:
//   - We don't have per-archetype counters in the match snapshot
//     (only total squadsBuilt[playerId]). Adding one would be a sim
//     change with test fallout.
//   - The HumanCommandSource is the natural choke point — every
//     produce_squad command the player issues passes through there. We
//     just count on the way past and persist.
//
// Storage: `signalclash.profile.squadMastery` as a JSON map
// archetype → count. Cloud-synced via CLOUD_SAVE_KEYS.

const STORAGE_KEY = 'signalclash.profile.squadMastery';

export type SquadArchetype = 'recon' | 'assault' | 'engineer' | 'shield';

export const ARCHETYPES: readonly SquadArchetype[] = ['recon', 'assault', 'engineer', 'shield'] as const;

export interface MasteryTier {
  id: 'trainee' | 'recruit' | 'veteran' | 'expert' | 'master';
  name: string;
  threshold: number;
}

export const MASTERY_TIERS: readonly MasteryTier[] = [
  { id: 'trainee', name: 'Trainee', threshold: 0 },
  { id: 'recruit', name: 'Recruit', threshold: 10 },
  { id: 'veteran', name: 'Veteran', threshold: 30 },
  { id: 'expert',  name: 'Expert',  threshold: 100 },
  { id: 'master',  name: 'Master',  threshold: 250 },
] as const;

// Per-archetype palette accent — matches the SquadDef visual identity
// used by the renderer. Surfaces in the ProfilePanel mastery row so the
// player visually maps "blue bar = my Recon mastery."
export const ARCHETYPE_COLOR: Record<SquadArchetype, string> = {
  recon:    '#6fdcff',
  assault:  '#ff7a5a',
  engineer: '#ffe680',
  shield:   '#c66bff',
};

export const ARCHETYPE_LABEL: Record<SquadArchetype, string> = {
  recon:    'Recon',
  assault:  'Assault',
  engineer: 'Engineer',
  shield:   'Shield',
};

type MasteryCounts = Record<SquadArchetype, number>;

function emptyCounts(): MasteryCounts {
  return { recon: 0, assault: 0, engineer: 0, shield: 0 };
}

function readCounts(): MasteryCounts {
  if (typeof localStorage === 'undefined') return emptyCounts();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return emptyCounts();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyCounts();
    const out = emptyCounts();
    for (const a of ARCHETYPES) {
      const v = (parsed as Record<string, unknown>)[a];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        out[a] = Math.round(v);
      }
    }
    return out;
  } catch {
    return emptyCounts();
  }
}

function writeCounts(counts: MasteryCounts): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

// Increment one archetype by `n`. Called from HumanCommandSource's
// produce-squad path so the count tracks actual player input rather
// than the post-match snapshot (which can't disambiguate archetypes).
export function recordSquadBuilt(archetype: SquadArchetype, n = 1): MasteryProgress {
  const counts = readCounts();
  counts[archetype] = (counts[archetype] ?? 0) + Math.max(0, Math.round(n));
  writeCounts(counts);
  return masteryFor(archetype);
}

export interface MasteryProgress {
  archetype: SquadArchetype;
  count: number;
  tier: MasteryTier;
  nextTier: MasteryTier | null;
  toNext: number;
  ratio: number; // 0..1 within current tier
}

export function masteryFor(archetype: SquadArchetype): MasteryProgress {
  const counts = readCounts();
  const count = counts[archetype] ?? 0;
  return progressFor(archetype, count);
}

function progressFor(archetype: SquadArchetype, count: number): MasteryProgress {
  let tier: MasteryTier = MASTERY_TIERS[0]!;
  let nextTier: MasteryTier | null = null;
  for (let i = 0; i < MASTERY_TIERS.length; i++) {
    const t = MASTERY_TIERS[i]!;
    if (count >= t.threshold) {
      tier = t;
      nextTier = MASTERY_TIERS[i + 1] ?? null;
    }
  }
  if (nextTier === null) {
    return { archetype, count, tier, nextTier: null, toNext: 0, ratio: 1 };
  }
  const span = nextTier.threshold - tier.threshold;
  const into = count - tier.threshold;
  const ratio = span > 0 ? Math.max(0, Math.min(1, into / span)) : 1;
  return { archetype, count, tier, nextTier, toNext: Math.max(0, nextTier.threshold - count), ratio };
}

export function getAllMasteries(): Record<SquadArchetype, MasteryProgress> {
  const counts = readCounts();
  return {
    recon:    progressFor('recon',    counts.recon),
    assault:  progressFor('assault',  counts.assault),
    engineer: progressFor('engineer', counts.engineer),
    shield:   progressFor('shield',   counts.shield),
  };
}
